import {
  addDoc,
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { db, storage } from "../../../../backend/firebaseConfig";
import { calculateAmountToCharge } from "../websiteConstants";
import { getEstimatedTurnaroundDate } from "../utils/websiteTimeline";
import { assertChargeDatesAreOpen } from "../../../Accounts/MonthlyPayments/utils/closedChargeMonths";

const WEBSITE_PATH = ["Marketing", "Website", "Items"];
const websitesCollection = () => collection(db, ...WEBSITE_PATH);
const mapSnapshot = (snapshot) => ({ id: snapshot.id, ...snapshot.data() });
const trimValue = (value) => String(value ?? "").trim();
const auditCollection = (websiteId) =>
  collection(db, ...WEBSITE_PATH, websiteId, "AuditLogs");
const progressCollection = (websiteId) =>
  collection(db, ...WEBSITE_PATH, websiteId, "ProgressHistory");

const normaliseActor = (actor = {}) => ({
  uid: actor.uid || "",
  email: actor.email || "",
  displayName: actor.displayName || "",
});

const uploadWebsiteForm = async (websiteId, file) => {
  if (!file || !(file instanceof File)) return file || null;
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const fileRef = ref(
    storage,
    `Marketing/Website/${websiteId}/uploads/${Date.now()}-${safeName}`,
  );
  await uploadBytes(fileRef, file, { contentType: file.type });
  return {
    name: file.name,
    type: file.type || "application/octet-stream",
    size: file.size,
    storagePath: fileRef.fullPath,
    url: await getDownloadURL(fileRef),
  };
};

const buildPayload = (data) => ({
  dateStarted: trimValue(data.dateStarted),
  dateAdviserInformed: trimValue(data.dateAdviserInformed),
  adviserName: trimValue(data.adviserName),
  adviserEmail: trimValue(data.adviserEmail).toLowerCase(),
  arTsName: trimValue(data.arTsName),
  estimatedTurnaroundDate:
    trimValue(data.estimatedTurnaroundDate) ||
    getEstimatedTurnaroundDate(data.dateAdviserInformed),
  status: trimValue(data.status),
  assignedTo: trimValue(data.assignedTo),
  notes: trimValue(data.notes),
  domains: (Array.isArray(data.domains) ? data.domains : []).map((domain) => ({
    domainName: trimValue(domain.domainName),
    registrationType: trimValue(domain.registrationType),
    numberOfYears: trimValue(domain.numberOfYears),
    registrar: trimValue(domain.registrar) || "Namecheap",
    datePaid: trimValue(domain.datePaid),
    amountPaid: trimValue(domain.amountPaid),
    paidBy: trimValue(domain.paidBy),
    markup: trimValue(domain.markup) || "20",
    amountToCharge: calculateAmountToCharge(
      domain.amountPaid,
      trimValue(domain.markup) || "20",
    ),
    notes: trimValue(domain.notes),
    design: {
      liveDate: trimValue(domain.design?.liveDate),
      amountToCharge: trimValue(domain.design?.amountToCharge),
      chargeAcross: trimValue(domain.design?.chargeAcross),
    },
    hosting: { monthlyCharge: trimValue(domain.hosting?.monthlyCharge) },
  })),
});

const comparable = (value) => JSON.stringify(value ?? null);

const websiteChargeComponents = (website = {}) =>
  (website.domains || []).flatMap((domain, index) => {
    const components = [];
    if (trimValue(domain.amountPaid)) {
      components.push({
        key: `${index}:domain`,
        date: domain.datePaid || website.createdAt,
        value: {
          registrationType: trimValue(domain.registrationType),
          amountPaid: trimValue(domain.amountPaid),
          markup: trimValue(domain.markup),
        },
      });
    }
    if (trimValue(domain.design?.amountToCharge)) {
      components.push({
        key: `${index}:design`,
        date: domain.design?.liveDate || website.createdAt,
        value: {
          amountToCharge: trimValue(domain.design?.amountToCharge),
          chargeAcross: trimValue(domain.design?.chargeAcross),
        },
      });
    }
    if (trimValue(domain.hosting?.monthlyCharge)) {
      components.push({
        key: `${index}:hosting`,
        date: domain.design?.liveDate || website.createdAt,
        value: { monthlyCharge: trimValue(domain.hosting?.monthlyCharge) },
      });
    }
    return components;
  });

export const getChangedWebsiteChargeDates = (before = {}, after = {}) => {
  const previous = new Map(
    websiteChargeComponents(before).map((component) => [component.key, component]),
  );
  const next = new Map(
    websiteChargeComponents(after).map((component) => [component.key, component]),
  );
  const adviserChanged =
    trimValue(before.adviserEmail).toLowerCase() !==
      trimValue(after.adviserEmail).toLowerCase() ||
    trimValue(before.adviserName) !== trimValue(after.adviserName);

  return [...new Set([...previous.keys(), ...next.keys()])].flatMap((key) => {
    const oldComponent = previous.get(key);
    const newComponent = next.get(key);
    const changed =
      adviserChanged ||
      comparable(oldComponent?.value) !== comparable(newComponent?.value) ||
      oldComponent?.date !== newComponent?.date;
    return changed
      ? [oldComponent?.date, newComponent?.date].filter(Boolean)
      : [];
  });
};

const getChangedFields = (before = {}, after = {}) =>
  Object.keys(after).reduce((changes, field) => {
    if (comparable(before[field]) !== comparable(after[field])) {
      changes[field] = {
        before: before[field] ?? null,
        after: after[field] ?? null,
      };
    }
    return changes;
  }, {});

const auditPayload = ({ action, actor, changes }) => ({
  action,
  actor: normaliseActor(actor),
  changes,
  changedFields: Object.keys(changes),
  createdAt: serverTimestamp(),
  createdAtMs: Date.now(),
});

const progressPayload = (label, actor, notes = "") => ({
  label,
  notes: trimValue(notes),
  userEmail: actor?.email || "",
  userDisplayName: actor?.displayName || "",
  userUid: actor?.uid || "",
  createdAt: serverTimestamp(),
  createdAtMs: Date.now(),
});

export const createWebsite = async ({ formData, file, actor }) => {
  const payload = buildPayload(formData);
  await assertChargeDatesAreOpen(getChangedWebsiteChargeDates({}, payload));
  const websiteRef = await addDoc(websitesCollection(), {
    ...payload,
    websiteForm: null,
    createdBy: normaliseActor(actor),
    createdAt: serverTimestamp(),
    createdAtMs: Date.now(),
    updatedAt: serverTimestamp(),
  });

  try {
    const websiteForm = await uploadWebsiteForm(websiteRef.id, file);
    if (websiteForm) await updateDoc(websiteRef, { websiteForm });

    const changes = getChangedFields({}, { ...payload, websiteForm });
    const batch = writeBatch(db);
    batch.set(
      doc(auditCollection(websiteRef.id)),
      auditPayload({ action: "created", actor, changes }),
    );
    batch.set(
      doc(progressCollection(websiteRef.id)),
      progressPayload("New entry added", actor, payload.notes),
    );
    await batch.commit();
    return websiteRef.id;
  } catch (error) {
    await updateDoc(websiteRef, {
      uploadError: true,
      updatedAt: serverTimestamp(),
    });
    throw error;
  }
};

export const updateWebsite = async ({ websiteId, formData, file, actor }) => {
  const websiteRef = doc(db, ...WEBSITE_PATH, websiteId);
  const currentSnapshot = await getDoc(websiteRef);
  if (!currentSnapshot.exists()) throw new Error("Website entry not found.");

  const current = currentSnapshot.data();
  const nextPayload = buildPayload(formData);
  await assertChargeDatesAreOpen(
    getChangedWebsiteChargeDates(current, nextPayload),
  );
  const websiteForm =
    file instanceof File
      ? await uploadWebsiteForm(websiteId, file)
      : formData.websiteForm || current.websiteForm || null;
  const next = { ...nextPayload, websiteForm };
  const changes = getChangedFields(current, next);

  if (!Object.keys(changes).length) return;

  const batch = writeBatch(db);
  batch.update(websiteRef, {
    ...next,
    updatedAt: serverTimestamp(),
    updatedBy: normaliseActor(actor),
  });
  batch.set(
    doc(auditCollection(websiteId)),
    auditPayload({ action: "updated", actor, changes }),
  );

  if (Object.prototype.hasOwnProperty.call(changes, "status")) {
    const label = next.status ? `Moved to ${next.status}` : "Status cleared";
    batch.set(
      doc(progressCollection(websiteId)),
      progressPayload(label, actor, next.notes),
    );
  }

  await batch.commit();
};

export const removeWebsiteForm = async (websiteId, websiteForm, actor) => {
  if (websiteForm?.storagePath) {
    await deleteObject(ref(storage, websiteForm.storagePath)).catch(() => {});
  }

  const batch = writeBatch(db);
  batch.update(doc(db, ...WEBSITE_PATH, websiteId), {
    websiteForm: null,
    updatedAt: serverTimestamp(),
    updatedBy: normaliseActor(actor),
  });
  batch.set(
    doc(auditCollection(websiteId)),
    auditPayload({
      action: "file_removed",
      actor,
      changes: { websiteForm: { before: websiteForm || null, after: null } },
    }),
  );
  await batch.commit();
};

export const subscribeWebsites = (callback, onError) =>
  onSnapshot(
    query(websitesCollection(), orderBy("createdAtMs", "desc")),
    (snapshot) => callback(snapshot.docs.map(mapSnapshot)),
    onError,
  );

export const subscribeWebsite = (websiteId, callback, onError) =>
  onSnapshot(
    doc(db, ...WEBSITE_PATH, websiteId),
    (snapshot) => callback(snapshot.exists() ? mapSnapshot(snapshot) : null),
    onError,
  );

export const subscribeProgressHistory = (websiteId, callback, onError) =>
  onSnapshot(
    query(progressCollection(websiteId), orderBy("createdAtMs", "desc")),
    (snapshot) => callback(snapshot.docs.map(mapSnapshot)),
    onError,
  );

export const subscribeAuditLogs = (websiteId, callback, onError) =>
  onSnapshot(
    query(auditCollection(websiteId), orderBy("createdAtMs", "desc")),
    (snapshot) => callback(snapshot.docs.map(mapSnapshot)),
    onError,
  );
